// src/pages/PurchaseTickets.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios, { API_BASE } from "../services/api";
import SmartImg from "../components/SmartImg";

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  const stockPill = (remaining, reserved = 0) => {
    if (remaining <= 0) {
      // If leftover are held in reserve, communicate "Unavailable" (not "Sold out")
      return {
        text: reserved > 0 ? "Unavailable" : "Sold out",
        cls: reserved > 0 ? "bg-white/10 text-slate-200" : "bg-red-500/20 text-red-200",
      };
    }
    if (remaining <= 10) return { text: `${remaining} remaining`, cls: "bg-amber-500/20 text-amber-200" };
    return { text: "Available", cls: "bg-emerald-500/20 text-emerald-200" };
  };

  export default function PurchaseTickets() {
    const { eventId } = useParams();
    const [event, setEvent] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [user, setUser] = useState(null);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [affiliateCode, setAffiliateCode] = useState("");
    const [affiliateValid, setAffiliateValid] = useState(false);
    const [yearlyFreeLeft, setYearlyFreeLeft] = useState(0);
    const [freeUsage, setFreeUsage] = useState(null); // { used, max, remaining, year, unlimited? }
    const [usage, setUsage] = useState({ hasFreeForEvent: false });

    useEffect(() => {
      fetchEvent();
      fetchUser();
      fetchFreeTicketUsage();
    }, []);
    useEffect(() => {
      fetchUsage();
    }, [eventId]);

    const fetchEvent = async () => {
      try {
        const { data } = await axios.get("/api/events/list");
        const found = (data || []).find((e) => String(e.id) === String(eventId));
        setEvent(found || null);
        if (!found) setMessage("Event not found.");
      } catch {
        setMessage("Failed to load event.");
      }
    };

    const fetchUser = async () => {
      try {
        const { data } = await axios.get("/api/account/me");
        setUser(data);
      } catch (err) {
        console.error("Failed to load user info", err);
      }
    };

    const fetchUsage = async () => {
      try {
        const { data } = await axios.get(`/api/tickets/event-usage?eventId=${eventId}`);
        setUsage({ hasFreeForEvent: !!data?.hasFreeForEvent });
      } catch { }
    };

    const addTicket = (type) => {
      setTickets((prev) => [
        ...prev,
        { type, firstName: "", lastName: "", dateOfBirth: "", useFreeTicket: false },
      ]);
    };
    const removeTicket = (i) => setTickets((prev) => prev.filter((_, idx) => idx !== i));
    const updateTicket = (i, field, value) =>
      setTickets((prev) => prev.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));

    const isOver18 = (dob) => {
      if (!dob) return false;
      const d = new Date(dob);
      const today = new Date();
      const age = today.getFullYear() - d.getFullYear();
      const m = today.getMonth() - d.getMonth();
      return age > 18 || (age === 18 && (m > 0 || (m === 0 && today.getDate() >= d.getDate())));
    };

    const isVerified = user?.verificationStatus === "VERIFIED";
    const hasActiveSub =
      !!(
        user &&
        (user.subscriptionStatus === "ACTIVE" || user.subscriptionActive === true) &&
        user.subscriptionTier &&
        user.subscriptionTier !== "NONE"
      );
    const hasSubAndVerified = hasActiveSub && isVerified;

    // Align with backend's YEARLY allowance
    const fetchFreeTicketUsage = async () => {
      try {
        const { data } = await axios.get("/api/account/free-tickets");
        // expected: { used, max, remaining, year, unlimited? } — be defensive
        const unlimited =
          data?.unlimited === true ||
          data?.max === -1 ||
          (typeof data?.max === "number" && data.max > 1000) ||
          String(user?.role ?? "").toUpperCase() === "HEADLINER" ||
          ["subscriptionTier", "tier", "plan", "membership"]
            .some(k => String(user?.[k] ?? "").toUpperCase().includes("HEADLINER"));
        const remaining = unlimited ? Number.POSITIVE_INFINITY
          : (typeof data?.remaining === "number"
            ? Math.max(0, data.remaining)
            : Math.max(0, (Number(data?.max ?? 0) - Number(data?.used ?? 0))));
        setFreeUsage({ ...data, unlimited });
        setYearlyFreeLeft(remaining);
      } catch (e) {
        // fail-soft; keep 0 which will simply not offer free
        setFreeUsage(null);
        setYearlyFreeLeft(0);
      }
    };

    const indexOfFree = tickets.findIndex((t) => t.type === "STANDARD" && t.useFreeTicket);

    const totalPrice = useMemo(() => {
      if (!event) return "0.00";
      let total = 0;
      tickets.forEach((t, idx) => {
        const isStandard = t.type === "STANDARD";
        const isFreeHere =
          isStandard &&
          idx === indexOfFree &&
          hasSubAndVerified &&
          yearlyFreeLeft > 0 &&
          !usage.hasFreeForEvent;
        if (isFreeHere) return;
        const base = isStandard ? event.standardPrice : event.vipPrice;
        const discount = affiliateValid ? base * 0.1 : 0;
        total += base - discount;
      });
      return total.toFixed(2);
    }, [event, tickets, indexOfFree, affiliateValid, hasSubAndVerified, yearlyFreeLeft, usage.hasFreeForEvent]);

    const breakdown = useMemo(() => {
      if (!event) return [];
      return tickets.map((t, idx) => {
        const isStandard = t.type === "STANDARD";
        const isFreeHere =
          isStandard &&
          idx === indexOfFree &&
          hasSubAndVerified &&
          yearlyFreeLeft > 0 &&
          !usage.hasFreeForEvent;
        if (isFreeHere) {
          return { label: `${t.type} (Free Ticket)`, base: event.standardPrice, discount: 0, final: 0 };
        }
        const base = isStandard ? event.standardPrice : event.vipPrice;
        const discount = affiliateValid ? base * 0.1 : 0;
        return { label: t.type, base, discount, final: base - discount };
      });
    }, [event, tickets, indexOfFree, affiliateValid, hasSubAndVerified, yearlyFreeLeft, usage.hasFreeForEvent]);

    const reserve = async () => {
      setMessage("");
      if (tickets.length === 0) return setMessage("Please add at least one ticket.");

      for (const t of tickets) {
        if (!t.firstName || !t.lastName || !t.dateOfBirth) {
          return setMessage("Please fill in all ticket fields.");
        }
        if (!isOver18(t.dateOfBirth)) {
          return setMessage("All attendees must be at least 18 years old.");
        }
      }

      // Client-side sanity against inventory (server remains authoritative)
      const stdLeft = event?.remainingStandardTickets ?? 0;
      const vipLeft = event?.remainingVipTickets ?? 0;
      const stdCount = tickets.filter((t) => t.type === "STANDARD").length;
      const vipCount = tickets.filter((t) => t.type === "VIP").length;
      if (stdCount > stdLeft || vipCount > vipLeft) {
        return setMessage("Not enough tickets available for the selected types.");
      }

      // Free ticket constraints (one STANDARD free for this event)
      const freeCount = tickets.filter((t) => t.type === "STANDARD" && t.useFreeTicket).length;
      if (freeCount > 1) return setMessage("You can only apply one free STANDARD ticket per event.");
      if (usage.hasFreeForEvent && freeCount === 1) {
        return setMessage("You've already used your free ticket for this event.");
      }
      if (freeCount === 1 && (!hasSubAndVerified || yearlyFreeLeft <= 0)) {
        return setMessage(
          hasActiveSub && !isVerified
            ? "Free tickets unlock after age verification."
            : "You have no free tickets remaining this year."
        );
      }

      setLoading(true);
      try {
        const ticketRequests = tickets.map((t, idx) => ({
          ticketType: t.type,
          firstName: t.firstName,
          lastName: t.lastName,
          dateOfBirth: t.dateOfBirth,
          useFreeTicket:
            t.type === "STANDARD" &&
            idx === indexOfFree &&
            hasSubAndVerified &&
            yearlyFreeLeft > 0 &&
            !usage.hasFreeForEvent,
        }));

        const reserveUrl = `/api/tickets/reserve?eventId=${eventId}${affiliateValid && affiliateCode ? `&affiliateCode=${affiliateCode}` : ""
          }`;
        const { data: reservationToken } = await axios.post(reserveUrl, ticketRequests);

        const { data: checkoutUrl } = await axios.post(
          `/api/tickets/create-checkout-session?reservationToken=${reservationToken}`,
          {},
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );

        window.location.href = checkoutUrl;
      } catch (err) {
        setMessage(err.response?.data || "Reservation failed.");
      } finally {
        setLoading(false);
      }
    };

    if (!event) {
      return (
        <div className="page">
          <div className="card">{message || "Loading event…"}</div>
        </div>
      );
    }

    const stdLeft = event.remainingStandardTickets ?? 0;
    const vipLeft = event.remainingVipTickets ?? 0;
    const vipReserved = event.reservedVipTickets ?? 0;

    const stdSelected = tickets.filter((t) => t.type === "STANDARD").length;
    const vipSelected = tickets.filter((t) => t.type === "VIP").length;
    const canAddStandard = stdSelected < stdLeft;
    const canAddVip = vipSelected < vipLeft;

    return (
      <div className="page space-y-6">
        {/* HEADER / EVENT SUMMARY */}
        <section className="card">
          <div className="grid gap-6 md:grid-cols-[140px,1fr] md:items-center">
            {/* Poster */}
            <div className="md:row-span-2">
              {event.imageUrl ? (
                <SmartImg
                  src={event.imageUrl.startsWith("http") ? event.imageUrl : `${API_BASE}${event.imageUrl}`}
                  alt={event.title}
                  className="h-36 w-36 rounded-xl object-cover border border-white/10"
                  width={288}
                  height={288}
                />
              ) : (
                <div className="h-36 w-36 rounded-xl border border-white/10 bg-white/5 grid place-items-center text-[10px] text-slate-400">
                  No image
                </div>
              )}
            </div>

            {/* Title + time */}
            <div>
              <h1 className="font-display text-2xl md:text-3xl text-white">{event.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-300">
                <span className="badge">
                  {new Date(event.date).toLocaleDateString(undefined, {
                    weekday: "short",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <span className="badge">
                  {event.startTime} – {event.endTime}
                </span>
                {event.location && (
                  <span className="badge max-w-[16rem] truncate" title={event.location}>
                    {event.location}
                  </span>
                )}
                {(() => { const s = stockPill(stdLeft); return <span className={`badge ${s.cls}`}>Standard: {s.text}</span>; })()}
                {(() => { const s = stockPill(vipLeft, vipReserved); return <span className={`badge ${s.cls}`}>VIP: {s.text}</span>; })()}
              </div>
            </div>

            {/* Prices block */}
            <div className="md:justify-self-end flex gap-8">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Standard</div>
                <div className="font-display text-3xl text-white leading-none">{fmtGBP(event.standardPrice)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400">VIP</div>
                <div className="font-display text-3xl text-white leading-none">{fmtGBP(event.vipPrice)}</div>
              </div>
            </div>
          </div>

          {/* Notices */}
          {hasActiveSub && !isVerified && (
            <div className="mt-4 rounded-xl border border-amber-400/20 bg-amber-500/10 p-3 text-amber-200 text-sm">
              You’re subscribed, but age verification isn’t complete yet — free tickets unlock after verification.
            </div>
          )}
          {vipLeft === 0 && vipReserved > 0 && (
            <div className="mt-2 rounded-xl border border-white/10 bg-white/5 p-3 text-slate-300 text-sm">
              VIP tickets are currently unavailable.
            </div>
          )}
        </section>

        {/* AFFILIATE CODE */}
        <section className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-white">Affiliate code</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr,160px]">
            <input
              type="text"
              value={affiliateCode}
              onChange={(e) => {
                setAffiliateCode(e.target.value);
                setAffiliateValid(false);
                setMessage("");
              }}
              placeholder="Enter code (optional)"
              className="input"
            />
            <button
              onClick={async () => {
                if (!affiliateCode) return;
                try {
                  await axios.get(`/api/tickets/validate?code=${affiliateCode}`);
                  setAffiliateValid(true);
                  setMessage("Affiliate code applied.");
                } catch (err) {
                  setAffiliateValid(false);
                  setMessage(err.response?.data || "Invalid affiliate code.");
                }
              }}
              className="btn-primary"
              type="button"
            >
              Apply
            </button>
          </div>
          {affiliateValid && <p className="mt-2 text-sm text-green-200">10% discount applied.</p>}
        </section>

        {/* TICKET BUILDER */}
        <section className="card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-white">Tickets ({tickets.length})</h2>
            <div className="flex gap-2">
              <button
                disabled={!canAddStandard}
                onClick={() => addTicket("STANDARD")}
                className="btn-secondary disabled:opacity-50"
                type="button"
              >
                Add Standard
              </button>
              <button
                disabled={!canAddVip}
                onClick={() => addTicket("VIP")}
                className="btn-secondary disabled:opacity-50"
                type="button"
              >
                Add VIP
              </button>
            </div>
          </div>

          {/* Ticket forms */}
          <div className="mt-4 grid gap-3">
            {tickets.map((t, i) => {
              const anotherFreeSelected = indexOfFree !== -1 && indexOfFree !== i;
              const canToggleFree =
                t.type === "STANDARD" &&
                hasSubAndVerified &&
                yearlyFreeLeft > 0 &&
                !usage.hasFreeForEvent &&
                (!anotherFreeSelected || t.useFreeTicket);

              return (
                <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="font-semibold text-white">{t.type} ticket</div>
                    <button
                      onClick={() => removeTicket(i)}
                      className="text-sm text-red-300 hover:text-red-200"
                      type="button"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="field">
                      <label className="label">First name</label>
                      <input
                        className="input"
                        value={t.firstName}
                        onChange={(e) => updateTicket(i, "firstName", e.target.value)}
                        placeholder="Jane"
                      />
                    </div>
                    <div className="field">
                      <label className="label">Last name</label>
                      <input
                        className="input"
                        value={t.lastName}
                        onChange={(e) => updateTicket(i, "lastName", e.target.value)}
                        placeholder="Doe"
                      />
                    </div>
                    <div className="field sm:col-span-2">
                      <label className="label">Date of birth</label>
                      <input
                        type="date"
                        className="input"
                        value={t.dateOfBirth}
                        onChange={(e) => updateTicket(i, "dateOfBirth", e.target.value)}
                      />
                    </div>
                  </div>

                  {t.type === "STANDARD" && (
                    <label className={`mt-3 flex items-center gap-2 text-sm ${!canToggleFree ? "opacity-50" : ""}`}>
                      <input
                        type="checkbox"
                        checked={t.useFreeTicket}
                        onChange={(e) => updateTicket(i, "useFreeTicket", e.target.checked)}
                        disabled={!canToggleFree}
                      />
                      <span>
                        Use free ticket for this event
                        {Number.isFinite(yearlyFreeLeft) && ` (${Math.max(0, yearlyFreeLeft)} left this year)`}
                      </span>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* SUMMARY & CHECKOUT */}
        <section className="card">
          <div className="md:flex md:items-start md:justify-between gap-6">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white">Price breakdown</h2>
              <ul className="mt-3 space-y-2 text-sm text-slate-200">
                {breakdown.map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                  >
                    <span>{item.label}</span>
                    <span className="font-medium">
                      {item.final === 0
                        ? "FREE"
                        : `${fmtGBP(item.base)}${affiliateValid ? ` − ${fmtGBP(item.discount)} = ${fmtGBP(item.final)}` : ""
                        }`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 md:mt-0 md:w-64">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm text-slate-400">Total</div>
                <div className="font-display text-3xl text-white">{fmtGBP(Number(totalPrice))}</div>
                <button onClick={reserve} disabled={loading} className="btn-primary w-full mt-4 disabled:opacity-60">
                  {loading ? "Processing…" : "Proceed to checkout"}
                </button>
              </div>
            </div>
          </div>

          {message && <p className="mt-3 text-sm text-red-300">{message}</p>}
        </section>
      </div>
    );
  }
